import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink } from "react-router-dom";

import { isNone, isNotNoneString } from "~/common/assert";
import { remoteLog } from "~/common/logging";
import { isUser } from "~/common/user";
import { formatPhoneNumber } from "~/common/utils";
import { PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { LabelledValue } from "~/components/ui/labelled-value";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { RootState } from "~/store";
import type { TUser } from "~/types/user";

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
  };
};

type DoctorPageProps = PropsFromRedux & { intl: IntlShape };

class DoctorPage extends Component<DoctorPageProps> {
  componentDidCatch(e: Error) {
    remoteLog(e, "doctor_page");
  }

  componentDidMount() {
    setDocumentTitle(this.props.intl.formatMessage({ id: "top.menu.profile" }));
  }

  renderNotifications() {
    const notifications = [];

    if (this.props.user.telegram_notify && !isNone(this.props.user.telegram_notify)) {
      notifications.push("Telegram");
    }

    if (this.props.user.email_notify && !isNone(this.props.user.email_notify)) {
      notifications.push("Email");
    }

    if (notifications.length > 0) {
      return notifications.join(", ");
    } else {
      return <FormattedMessage id="not.selected" />;
    }
  }

  renderPreferences(preferences: TUser["preferences"]) {
    return (
      <ProfileSection heading={<FormattedMessage id="preferences" />} separator={false}>
        <h4 className="tw-font-bold">
          <FormattedMessage id="preferences.ipr" />
        </h4>

        <div className="row">
          <div className="col-md-4">
            {preferences.perform_ipr == null ? (
              <p>
                <FormattedMessage id="None" />
              </p>
            ) : (
              <LabelledValue
                className="form-group"
                label={<FormattedMessage id="preferences.ipr_enabled" />}
              >
                <FormattedMessage id={`bool.${preferences.perform_ipr}`} />
              </LabelledValue>
            )}
          </div>

          {!preferences.perform_ipr || preferences.ipr_start_step == null ? null : (
            <LabelledValue
              className="col-md-4 form-group"
              label={<FormattedMessage id="preferences.ipr_start_step" />}
            >
              {preferences.ipr_start_step}
            </LabelledValue>
          )}
        </div>

        <h4 className="tw-font-bold" style={{ marginTop: 32 }}>
          <FormattedMessage id="preferences.attachments" />
        </h4>

        {preferences.place_attachments_on_step == null ? null : (
          <LabelledValue
            className="form-group"
            label={<FormattedMessage id="preferences.attachments_delay" />}
          >
            {preferences.place_attachments_on_step}
          </LabelledValue>
        )}

        <h4 className="tw-font-bold" style={{ marginTop: 32 }}>
          <FormattedMessage id="preferences.expansion" />
        </h4>

        {preferences.always_expand == null ? (
          <p>
            <FormattedMessage id="None" />
          </p>
        ) : (
          <LabelledValue
            className="form-group"
            label={<FormattedMessage id="preferences.always_expand" />}
          >
            <FormattedMessage id={`bool.${preferences.always_expand}`} />
          </LabelledValue>
        )}

        <h4 className="tw-font-bold" style={{ marginTop: 32 }}>
          <FormattedMessage id="preferences.incisor_relationship" />
        </h4>

        <div className="row">
          {preferences.incisor_relationship_overjet == null ? null : (
            <LabelledValue
              className="col-md-4 form-group"
              label={<FormattedMessage id="preferences.incisor_relationship.overjet" />}
            >
              {preferences.incisor_relationship_overjet}
            </LabelledValue>
          )}

          {preferences.incisor_relationship_overbite == null ? null : (
            <LabelledValue
              className="col-md-4 form-group"
              label={<FormattedMessage id="preferences.incisor_relationship.overbite" />}
            >
              {preferences.incisor_relationship_overbite}
            </LabelledValue>
          )}

          {preferences.upper_incisor_angulation == null ? null : (
            <LabelledValue
              className="col-md-4 form-group"
              label={<FormattedMessage id="preferences.upper_incisor_angulation" />}
            >
              {preferences.upper_incisor_angulation}
            </LabelledValue>
          )}
        </div>

        <h4 className="tw-font-bold" style={{ marginTop: 32 }}>
          <FormattedMessage id="preferences.overcorrection" />
        </h4>

        <div className="row">
          <LabelledValue
            className="col-md-4 form-group"
            label={<FormattedMessage id="preferences.deep_bite_intrusion" />}
          >
            {preferences.deep_bite_intrusion == null ? (
              <FormattedMessage id="None" />
            ) : (
              <FormattedMessage id={`bool.${preferences.deep_bite_intrusion}`} />
            )}
          </LabelledValue>

          <LabelledValue
            className="col-md-4 form-group"
            label={<FormattedMessage id="preferences.incisors_extrusion" />}
          >
            {preferences.incisors_extrusion == null ? (
              <FormattedMessage id="None" />
            ) : (
              <FormattedMessage id={`bool.${preferences.incisors_extrusion}`} />
            )}
          </LabelledValue>

          <LabelledValue
            className="col-md-4 form-group"
            label={<FormattedMessage id="preferences.rotation_over_30dg" />}
          >
            {preferences.rotation_over_30dg == null ? (
              <FormattedMessage id="None" />
            ) : (
              <FormattedMessage id={`bool.${preferences.rotation_over_30dg}`} />
            )}
          </LabelledValue>
        </div>

        <h4 className="tw-font-bold" style={{ marginTop: 32 }}>
          <FormattedMessage id="preferences.additional_notes" />
        </h4>

        {preferences.special_instructions == null ? null : (
          <div className="form-group">
            <p>{preferences.special_instructions}</p>
          </div>
        )}
      </ProfileSection>
    );
  }

  render() {
    const { user } = this.props;

    if (!isUser(user)) {
      return (
        <Layout>
          <Loader />
        </Layout>
      );
    }

    return (
      <Layout>
        <Portlet as="main">
          <PortletTitle
            iconClassName="icon-user"
            floatRightSlot={
              <Button asChild>
                <NavLink to="/pages/edit_my_account">
                  <FormattedMessage id="BTN_EDIT" />
                </NavLink>
              </Button>
            }
          >
            <FormattedMessage id="top.menu.profile" />
          </PortletTitle>

          <div className="portlet-body tw-p-0">
            <ProfileSection heading={<FormattedMessage id="doctor.profile.info" />}>
              <LabelledValue label={<FormattedMessage id="login.login" />}>
                {user.login}
              </LabelledValue>

              <LabelledValue label={<FormattedMessage id="doctor.fullname" />}>
                <PersonName person={user} isDoctor useMiddleName />
              </LabelledValue>

              <div className="row">
                <LabelledValue className="col-md-4" label="Email">
                  {user.email}
                </LabelledValue>

                {isNotNoneString(user.phone) ? (
                  <LabelledValue
                    className="col-md-4"
                    label={<FormattedMessage id="clin_table.phone" />}
                  >
                    {formatPhoneNumber(user.phone)}
                  </LabelledValue>
                ) : null}

                {isNotNoneString(user.telegram_id) ? (
                  <LabelledValue className="col-md-4" label="Telegram ID">
                    {user.telegram_id}
                  </LabelledValue>
                ) : null}
              </div>

              <LabelledValue label={<FormattedMessage id="notify.doctor.subscribe" />}>
                {this.renderNotifications()}
              </LabelledValue>
            </ProfileSection>

            {user.manager ? (
              <ProfileSection heading={<FormattedMessage id="doctor.manager" />}>
                <div className="row">
                  <LabelledValue
                    className="col-md-4"
                    label={<FormattedMessage id="doctor.fullname" />}
                  >
                    <PersonName person={user.manager} />
                  </LabelledValue>

                  {isNotNoneString(user.manager.phone) ? (
                    <LabelledValue
                      className="col-md-4"
                      label={<FormattedMessage id="clin_table.phone" />}
                    >
                      {formatPhoneNumber(user.manager.phone)}
                    </LabelledValue>
                  ) : null}
                </div>
              </ProfileSection>
            ) : null}

            {this.renderPreferences(user.preferences)}
          </div>

          <Button asChild>
            <NavLink to="/pages/edit_my_account">
              <FormattedMessage id="BTN_EDIT" />
            </NavLink>
          </Button>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(DoctorPage));

function ProfileSection({
  heading,
  children,
  separator = true,
}: React.PropsWithChildren<{ heading: React.ReactChild; separator?: boolean }>) {
  return (
    <section>
      <h3>{heading}</h3>
      <div className="tw-py-5">{children}</div>
      {separator ? <hr className="tw-m-0" /> : null}
    </section>
  );
}
